import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  User,
  MessageCircle,
  Calendar,
  Camera,
  Video,
  Heart,
  Building,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'

interface ContactForm {
  name: string
  email: string
  phone: string
  subject: string
  service: string
  eventDate: string
  budget: string
  message: string
}

const Contact = () => {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service: '',
    eventDate: '',
    budget: '',
    message: '',
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errors, setErrors] = useState<Partial<ContactForm>>({})

  const services = [
    { id: 'wedding', name: 'Wedding Photography & Videography', icon: Heart },
    { id: 'corporate', name: 'Corporate Events', icon: Building },
    { id: 'portrait', name: 'Portrait Photography', icon: User },
    { id: 'event', name: 'Event Management', icon: Calendar },
    { id: 'video', name: 'Video Production', icon: Video },
    { id: 'photography', name: 'Photography Services', icon: Camera },
  ]

  const budgetRanges = [
    'Small Event',
    'Medium Event',
    'Large Event',
    'Premium Event',
    'Luxury Event',
    'Custom Quote Required',
  ]

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['+91 787446 7874'],
      action: 'tel:+917874467874',
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['bhavesh7874@gmail.com'],
      action: 'mailto:bhavesh7874@gmail.com',
    },
    {
      icon: MapPin,
      title: 'Address',
      details: ['Ahmedabad, Gujarat', 'India'],
      action: 'https://maps.google.com/maps?q=Ahmedabad,+Gujarat,+India',
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Mon - Sat: 9:00 AM - 8:00 PM', 'Sunday: 10:00 AM - 6:00 PM', '24/7 Emergency Support'],
      action: null,
    },
  ]

  const socialLinks = [
    { icon: Facebook, name: 'Facebook', url: 'https://facebook.com/parthvideo', color: 'text-blue-500' },
    { icon: Instagram, name: 'Instagram', url: 'https://instagram.com/parthvideo', color: 'text-pink-500' },
    { icon: Youtube, name: 'YouTube', url: 'https://youtube.com/parthvideo', color: 'text-red-500' },
    { icon: Twitter, name: 'Twitter', url: 'https://twitter.com/parthvideo', color: 'text-blue-400' },
    { icon: Linkedin, name: 'LinkedIn', url: 'https://parthvideo.in', color: 'text-blue-600' },
  ]

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactForm> = {}

    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!/^[\d\s\+\-\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number'
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required'
    if (!formData.service) newErrors.service = 'Please select a service'
    if (!formData.message.trim()) newErrors.message = 'Message is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear error when user starts typing
    if (errors[name as keyof ContactForm]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('http://localhost:5001/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error('Failed to submit contact form')
      }

      const result = await response.json()
      console.log('Contact form submitted successfully:', result)
      
      setSubmitStatus('success')
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        service: '',
        eventDate: '',
        budget: '',
        message: '',
      })
    } catch (error) {
      console.error('Contact form submission error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-24"
    >
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Get In <span className="gradient-text">Touch</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto leading-relaxed">
              Ready to capture your special moments? Let's discuss your project and bring your vision to life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                className="glass-card"
              >
                <div className="mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    Send us a <span className="gradient-text">Message</span>
                  </h2>
                  <p className="text-white/70">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`glass-input w-full ${
                          errors.name ? 'border-red-500' : ''
                        }`}
                        placeholder="Your full name"
                      />
                      {errors.name && (
                        <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`glass-input w-full ${
                          errors.email ? 'border-red-500' : ''
                        }`}
                        placeholder="your.email@example.com"
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`glass-input w-full ${
                          errors.phone ? 'border-red-500' : ''
                        }`}
                        placeholder="+91 98765 43210"
                      />
                      {errors.phone && (
                        <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className={`glass-input w-full ${
                          errors.subject ? 'border-red-500' : ''
                        }`}
                        placeholder="Project inquiry"
                      />
                      {errors.subject && (
                        <p className="text-red-400 text-sm mt-1">{errors.subject}</p>
                      )}
                    </div>
                  </div>

                  {/* Service Selection */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Service Required *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className={`glass-input w-full ${
                        errors.service ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                    {errors.service && (
                      <p className="text-red-400 text-sm mt-1">{errors.service}</p>
                    )}
                  </div>

                  {/* Event Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Event Date
                      </label>
                      <input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleInputChange}
                        className="glass-input w-full"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Budget Range
                      </label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="glass-input w-full"
                      >
                        <option value="">Select budget range</option>
                        {budgetRanges.map((range) => (
                          <option key={range} value={range}>
                            {range}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      className={`glass-input w-full resize-none ${
                        errors.message ? 'border-red-500' : ''
                      }`}
                      placeholder="Tell us about your project, vision, and any specific requirements..."
                    />
                    {errors.message && (
                      <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="flex items-center space-x-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full px-8 py-4 font-medium transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Send Message</span>
                        </>
                      )}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="flex items-center space-x-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span>Message sent successfully!</span>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="flex items-center space-x-2 text-red-400">
                        <AlertCircle className="w-5 h-5" />
                        <span>Failed to send message. Please try again.</span>
                      </div>
                    )}
                  </div>
                </form>
              </motion.div>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="glass-card"
              >
                <h3 className="text-xl font-bold text-white mb-6">
                  Contact <span className="gradient-text">Information</span>
                </h3>
                
                <div className="space-y-6">
                  {contactInfo.map((info, index) => {
                    const Icon = info.icon
                    return (
                      <motion.div
                        key={index}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start space-x-4"
                      >
                        <div className="p-3 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex-shrink-0">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-medium mb-2">{info.title}</h4>
                          <div className="space-y-1">
                            {info.details.map((detail, detailIndex) => (
                              <p key={detailIndex} className="text-white/70 text-sm">
                                {info.action && detailIndex === 0 ? (
                                  <a
                                    href={info.action}
                                    className="hover:text-primary-300 transition-colors duration-300"
                                  >
                                    {detail}
                                  </a>
                                ) : (
                                  detail
                                )}
                              </p>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>

              {/* Social Media */}
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="glass-card"
              >
                <h3 className="text-xl font-bold text-white mb-6">
                  Follow <span className="gradient-text">Us</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 p-3 rounded-lg glass hover:bg-white/20 transition-all duration-300 hover-lift"
                      >
                        <Icon className={`w-5 h-5 ${social.color}`} />
                        <span className="text-white/80 text-sm">{social.name}</span>
                      </a>
                    )
                  })}
                </div>
              </motion.div>

              {/* Quick Contact */}
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="glass-card"
              >
                <h3 className="text-xl font-bold text-white mb-6">
                  Quick <span className="gradient-text">Contact</span>
                </h3>
                
                <div className="space-y-4">
                  <a
                    href="tel:+917874467874"
                    className="flex items-center space-x-3 p-4 rounded-lg bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 hover:bg-green-500/30 transition-all duration-300"
                  >
                    <Phone className="w-5 h-5 text-green-400" />
                    <div>
                      <div className="text-white font-medium">Call Now</div>
                      <div className="text-green-400 text-sm">+91 787446 7874</div>
                    </div>
                  </a>
                  
                  <a
                    href="https://wa.me/917874467874"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 rounded-lg bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 hover:bg-green-500/30 transition-all duration-300"
                  >
                    <MessageCircle className="w-5 h-5 text-green-400" />
                    <div>
                      <div className="text-white font-medium">WhatsApp</div>
                      <div className="text-green-400 text-sm">Quick Response</div>
                    </div>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="glass-card"
          >
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Find Us in <span className="gradient-text">Ahmedabad</span>
              </h2>
              <p className="text-white/70">
                Located in the heart of Ahmedabad, Gujarat. Contact us to schedule a meeting.
              </p>
            </div>
            
            <div className="aspect-video rounded-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d235013.74842572804!2d72.41493118359375!3d23.020474299999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e848aba5bd449%3A0x4fcedd11614f6516!2sAhmedabad%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Parth Video Location - Ahmedabad, Gujarat"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Quick answers to common questions about our services
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                question: 'How far in advance should I book?',
                answer: 'We recommend booking at least 2-3 months in advance, especially for weddings and major events during peak season.',
              },
              {
                question: 'Do you travel for destination events?',
                answer: 'Yes, we travel anywhere in India and internationally. Travel costs will be included in the quote.',
              },
              {
                question: 'What is included in your packages?',
                answer: 'Our packages include professional photography/videography, editing, online gallery, and delivery of final files.',
              },
              {
                question: 'How long does editing take?',
                answer: 'Typically 2-4 weeks for weddings and 1-2 weeks for other events, depending on the scope of work.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card"
              >
                <h3 className="text-white font-semibold mb-3">{faq.question}</h3>
                <p className="text-white/70 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  )
}

export default Contact